import type { AppDispatch } from '@/redux/store';
import type { LicenseByZipParamsInterface } from '@/model/explore';
import { setLicenseByState, setLicenseByZipQueryParams } from '@/explore/ducks/slice';

/**
 * Stored inside dealer explore `search_filters` JSON alongside sort/filter params.
 * Stripped before merging into Redux `licenseByZipQueryParams` (never sent as API query params).
 * Use a plain key (no leading __) — some APIs/ORMs strip underscore-prefixed keys on save.
 */
export const EXPLORE_DEALER_SAVED_FILTER_SEARCH_META = 'exploreDealerSearchMeta' as const;

/** Legacy key for collections saved before the rename; still read on apply. */
export const EXPLORE_DEALER_SAVED_FILTER_SEARCH_META_LEGACY =
  '__exploreDealerSearchMeta' as const;

export type ExploreDealerSavedSearchMeta = {
  query: string[];
  recognizerKind: string;
};

/**
 * GET /dealers/filter/collections may return a bare array or a wrapped payload.
 * Always normalize to an array before storing in redux or mapping in the UI.
 */
export function coerceSavedFilterCollectionsToArray(value: unknown): any[] {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    const o = value as Record<string, unknown>;
    if (Array.isArray(o.data)) return o.data;
    if (Array.isArray(o.collections)) return o.collections;
    if (Array.isArray(o.items)) return o.items;
    if (Array.isArray(o.results)) return o.results;
  }
  return [];
}

/** First collection marked default (API: `is_default` or UI: `isDefault`). */
export function getDefaultSavedFilterCollection(collections: unknown): any | null {
  const list = coerceSavedFilterCollectionsToArray(collections);
  return (
    list.find((f: any) => f.is_default === true || f.isDefault === true) ?? null
  );
}

/** True when payload looks like ag-grid column filter map (not explore sort/filter params). */
function isAgGridStyleSearchFilters(obj: Record<string, unknown>): boolean {
  return Object.values(obj).some((v) => {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
    return 'filterType' in (v as Record<string, unknown>);
  });
}

/** API may return JSON string; .NET may use PascalCase for some fields. */
function normalizeExploreSearchFiltersShape(
  input: Record<string, unknown>
): Record<string, unknown> {
  const o = { ...input };
  if (o.state == null && o.State != null) {
    o.state = o.State;
    delete o.State;
  }
  if (o.sortBy == null && typeof o.SortBy === 'string') {
    o.sortBy = o.SortBy;
    delete o.SortBy;
  }
  return o;
}

/**
 * Parse `search_filters` from GET/PUT (string JSON or object) into a flat record for apply/save UI.
 */
export function coerceExploreSavedSearchFiltersFromApi(
  raw: unknown
): Record<string, unknown> | null {
  let obj: unknown = raw;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return null;
    try {
      obj = JSON.parse(t) as unknown;
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return null;
  }
  return normalizeExploreSearchFiltersShape(obj as Record<string, unknown>);
}

function parseExploreDealerSearchMetaRaw(
  raw: unknown
): ExploreDealerSavedSearchMeta | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const q = o.query;
  const queryStrings = Array.isArray(q)
    ? q.map((x) => (x == null ? '' : String(x)))
    : [];
  const kind =
    typeof o.recognizerKind === 'string'
      ? o.recognizerKind
      : typeof o.RecognizerKind === 'string'
        ? o.RecognizerKind
        : null;
  if (!queryStrings.length || !kind) {
    return null;
  }
  return { query: queryStrings, recognizerKind: kind };
}

export function stripExploreDealerSearchMetaFromSearchFilters(
  searchFilters: Record<string, unknown>
): { rest: Record<string, unknown>; meta: ExploreDealerSavedSearchMeta | null } {
  const copy = { ...searchFilters };
  const rawMeta =
    copy[EXPLORE_DEALER_SAVED_FILTER_SEARCH_META] ??
    copy[EXPLORE_DEALER_SAVED_FILTER_SEARCH_META_LEGACY];
  delete copy[EXPLORE_DEALER_SAVED_FILTER_SEARCH_META];
  delete copy[EXPLORE_DEALER_SAVED_FILTER_SEARCH_META_LEGACY];
  const meta = parseExploreDealerSearchMetaRaw(rawMeta);
  return { rest: copy, meta };
}

/** Merge API filter params with optional persisted search (query + recognizer). */
export function buildExploreDealerSavedSearchFiltersPayload(
  paramsWithState: Record<string, unknown>,
  searchMeta: ExploreDealerSavedSearchMeta | null | undefined
): Record<string, unknown> {
  const base = { ...paramsWithState };
  delete base[EXPLORE_DEALER_SAVED_FILTER_SEARCH_META_LEGACY];
  if (searchMeta?.query?.length && searchMeta.recognizerKind) {
    base[EXPLORE_DEALER_SAVED_FILTER_SEARCH_META] = searchMeta;
  } else {
    delete base[EXPLORE_DEALER_SAVED_FILTER_SEARCH_META];
  }
  return base;
}

/**
 * Applies a saved collection's `search_filters` to explore redux so dealer search refetches.
 * Expects the same flat shape produced when saving (LicenseSortOptions + optional `state` + optional search meta).
 * @returns persisted search meta for the caller to apply query + recognizer (Redux), or null.
 */
export function applySavedSearchFiltersToExploreState(
  dispatch: AppDispatch,
  searchFilters: unknown
): ExploreDealerSavedSearchMeta | null {
  const coerced = coerceExploreSavedSearchFiltersFromApi(searchFilters);
  if (!coerced) {
    return null;
  }
  if (isAgGridStyleSearchFilters(coerced)) {
    return null;
  }
  const { rest: withoutMeta, meta } = stripExploreDealerSearchMetaFromSearchFilters(coerced);
  const { state, ...rest } = withoutMeta;
  if (Array.isArray(state)) {
    dispatch(setLicenseByState(state as string[]));
  } else {
    dispatch(setLicenseByState([]));
  }
  dispatch(
    setLicenseByZipQueryParams({
      sortBy: 'preferred',
      ...(rest as LicenseByZipParamsInterface)
    })
  );
  return meta;
}
